class EnhancedTable {
  id = null;
  footerOptions = null;
  $originalTableSnapshot = null;

  static render = {
    dateTime: (showTimeZone = false) => {
      return (data, type) => {
        if (!data) return null;
        if (type === "sort") return new Date(data).getTime();
        return Formatter.toTableDateTime(data, showTimeZone);
      };
    },
    number: {
      suffix: (unit) => {
        return (data, type) => {
          if ([undefined, null].includes(data)) return data;
          const dataAsNumber = Number.parseFloat(data);
          if (type === "sort") return dataAsNumber;
          return `${dataAsNumber}${unit}`;
        };
      },
    },
  };

  static TRANSFORMED_ITEM = Symbol("TRANSFORMED_ITEM");

  constructor({id, instance, footerOptions, $originalTableSnapshot}) {
    this.id = id;
    this.footerOptions = footerOptions;
    this.$originalTableSnapshot = $originalTableSnapshot;
    Object.assign(this, instance);
    Object.setPrototypeOf(this.init(), this);
  }

  getSelectedItem() {
    const SELECTED_ITEM_SELECTOR = ".table-item--selected";
    const item = this.row(SELECTED_ITEM_SELECTOR);
    if (!item.any()) return {notFound: true};
    const outputFormatter = this.settings()[0].oInit?.formatter?.out ?? ((item) => item);
    const data = item.data();
    return {
      type: "row",
      $node: item.node(),
      data: !data ? data : outputFormatter(structuredClone(data)),
      index: item.index(),
      notFound: false,
      remove: (() => {
        const dataList = this.data().toArray();
        dataList.splice(item.index(), 1);
        this.render(dataList, {skipFormatting: true});
      }).bind(this),
      modify: ((itemOverride) => {
        const dataList = this.data().toArray();
        const oldItem = item.data();
        const newItem = {...oldItem, ...itemOverride};
        const newFormattedItem = this.formatItem(newItem);
        dataList.splice(item.index(), 1, newFormattedItem);
        this.render(dataList, {skipFormatting: true});
        return newFormattedItem;
      }).bind(this),
      unselect: (() => item.unselect()).bind(this),
    };
    // The `.bind(this)` is a precaution for future updates, that the 'this' value will not be lost. Please, maintain them (I know, they're ugly).
  }

  addItem(item) {
    if (Array.isArray(item)) {
      const errorMsg = `EnhancedTable.addItem(): Item must be an object, array received "${JSON.stringify(value)}".`;
      return console.error(errorMsg);
    }
    const dataList = this.data().toArray();
    const formattedItem = this.formatItem(item);
    dataList.push(formattedItem);
    this.render(dataList, {skipFormattin: true});
  }

  modifyItem(itemOverride, itemMatchFn) {
    if (Array.isArray(itemOverride)) {
      const errorMsg = `EnhancedTable.modifyItem(): Item override must be an object, array received "${JSON.stringify(value)}".`;
      return console.error(errorMsg);
    }
    if (typeof itemMatchFn !== "function") {
      const errorMsg = `EnhancedTable.modifyItem(): Item match function must be a function, received "${itemMatchFn}".`;
      return console.error(errorMsg);
    }
    const dataList = this.data().toArray();
    const oldItemIndex = dataList.findIndex(itemMatchFn);
    if (oldItemIndex === -1) {
      const errorMsg = `EnhancedTable.modifyItem(): We could not find the old item to modify with the current query "${query}".`;
      return console.error(errorMsg);
    }
    const oldItem = dataList[oldItemIndex];
    const newItem = {...oldItem, ...itemOverride};
    const newFormattedItem = this.formatItem(newItem);
    dataList.splice(oldItemIndex, 1, newFormattedItem);
    this.render(dataList, {skipFormatting: true});
  }

  removeItem(itemMatchFn) {
    if (typeof itemMatchFn !== "function") {
      const errorMsg = `EnhancedTable.removeItem(): Item match function must be a function, received "${itemMatchFn}".`;
      return console.error(errorMsg);
    }
    const dataList = this.data().toArray();
    const oldItemIndex = dataList.findIndex(itemMatchFn);
    if (oldItemIndex === -1) {
      const errorMsg = `EnhancedTable.removeItem(): We could not find the old item to remove with the current query "${query}".`;
      return console.error(errorMsg);
    }
    dataList.splice(oldItemIndex, 1);
    this.render(dataList, {skipFormatting: true});
  }

  formatItem(item) {
    const inputFormatter = this.settings()[0].oInit?.formatter?.in ?? ((item) => item);
    return inputFormatter(structuredClone(item));
  }

  render(dataList, {skipFormatting} = {}) {
    if (!dataList || !Array.isArray(dataList))
      throw new Error(
        `EnhancedTable.render(): You cannot call render without a valid array of data, you had passed: "${dataList}".`,
      );
    const formattedDataList = skipFormatting ? dataList : dataList.map((item) => this.formatItem(item));
    this.rows().invalidate();
    this.clear();
    this.rows.add(formattedDataList).draw();
  }

  renderFooter() {
    if (!this.footerOptions) {
      const errorMsg =
        "EnhancedTable.renderFooter(): No footerOptions found. It is passed as a property named 'footer' when creating a table with the 'createTable' method.";
      throw new Error(errorMsg);
    }
    const api = this.table();
    const $table = api.node();
    const visibleRowsIndexes = api.rows({page: "current"}).indexes().toArray();
    const allVisibleColumns = api.columns()[0].filter((columnIndex) => api.column(columnIndex).visible());
    handler(api, $table, visibleRowsIndexes, allVisibleColumns, this.footerOptions);
    return;

    function handler(api, $table, visibleRowsIndexes, allVisibleColumns, footerOptions) {
      const {rows: footerRows = [], aggregators = []} = footerOptions;
      removeFooter($table);
      if (!visibleRowsIndexes.length || !allVisibleColumns.length) return;
      const $footer = renderFooter($table);
      const $footerRows = renderFooterRows(api, $footer, footerRows);
      const columnsDataByAggregator = getColumnsDataByAggregator(api, aggregators, visibleRowsIndexes);
      const aggregatedColumnsData = getAggregatedColumnsData(columnsDataByAggregator);
      const footerData = getFooterData(aggregatedColumnsData, footerRows);
      populateFooterRows($footerRows, footerRows, footerData);
      return;

      function removeFooter($table) {
        const $footer = $table.querySelector("tfoot");
        if ($footer) $footer.remove();
      }

      function renderFooter($table) {
        const $footer = document.createElement("tfoot");
        $table.appendChild($footer);
        return $footer;
      }

      function renderFooterRows(api, $footer, footerRows) {
        const $footerRows = Object.keys(footerRows).map(() => document.createElement("tr"));
        $footerRows.forEach(($row) => {
          const $cells = allVisibleColumns.map((columnIndex) => {
            const $tr = document.createElement("th");
            const currentColumnSettings = api.settings()[0].aoColumns[columnIndex];
            const classNameSetting = currentColumnSettings.className ?? "";
            $tr.setAttribute("class", classNameSetting);
            return $tr;
          });
          $row.append(...$cells);
          $footer.appendChild($row);
        });
        return $footerRows;
      }

      function getColumnsDataByAggregator(api, aggregators, visibleRowsIndexes) {
        const allRowsIndexes = api.rows()[0];
        const visibleRowsData = visibleRowsIndexes.map((renderedIndex) => {
          const rowIndex = allRowsIndexes[renderedIndex];
          const row = api.row(rowIndex);
          return row.data();
        });
        const columnsWithAggregators = allVisibleColumns.map((columnIndex, visibleIndex) => {
          const columnsDefinition = api.settings()[0].aoColumns;
          const currentColumnSettings = columnsDefinition[columnIndex];
          const rowAggregatorName = currentColumnSettings.aggregator;
          if (!rowAggregatorName) return null;
          const aggregator = aggregators[rowAggregatorName];
          if (!aggregator) {
            const errorMsg = `EnhancedTable.renderFooter(): It is set at column "${visibleIndex}" a footer aggregator named "${rowAggregatorName}" of which was not found among the expected aggregators registered in \`footer.aggregators\` instance property.`;
            console.error(errorMsg);
            throw new Error(errorMsg);
          }

          return {
            columnIndex: visibleIndex,
            aggregator,
            aggregatorName: rowAggregatorName,
            rowsData: visibleRowsData,
            columnData: currentColumnSettings,
          };
        });
        const filteredColumns = columnsWithAggregators.filter((column) => column);
        return filteredColumns;
      }

      function getAggregatedColumnsData(columnsDataByAggregator) {
        return columnsDataByAggregator.map(({columnIndex, aggregator, aggregatorName, rowsData, columnData}) => {
          const aggregatedRowsData = aggregator(rowsData, columnData) ?? {};
          return {columnIndex, aggregatorName, aggregatedData: aggregatedRowsData};
        });
      }

      function getFooterData(aggregatedColumnsData, footerRows) {
        const footerDataEntries = aggregatedColumnsData.map(({columnIndex, aggregatorName, aggregatedData}) => {
          const unsupportedData =
            ["string", "boolean", "undefined", "null", "number", "function"].includes(typeof aggregatedData) ||
            Array.isArray(aggregatedData) ||
            aggregatedData instanceof Date;
          if (unsupportedData) {
            const errorMsg = `EnhancedTable.renderFooter: The footer aggregator "${aggregatorName}" at column "${columnIndex}" produced unsupported data: "${aggregatedData}". The expected type is an object with the properties registered in \`footer.rows\` instance property."`;
            throw new Error(errorMsg);
          }
          const aggregatedDataEntries = Object.entries(aggregatedData);
          const footerRowsData = aggregatedDataEntries.map(([footerRowName, data]) => {
            const footerRowInfo = footerRows[footerRowName];
            if (!footerRowInfo) {
              const errorMsg = `EnhancedTable.renderFooter: The footer aggregator "${aggregatorName}" of column "${columnIndex}" produced data for the footer row "${footerRowName}" of which was not found among the expected rows registered in \`footer.rows\` instance property."`;
              throw new Error(errorMsg);
            }
            return {rowName: footerRowName, content: data};
          });
          return [columnIndex, footerRowsData];
        });
        return Object.fromEntries(footerDataEntries);
      }

      function populateFooterRows($footerRows, footerRows, footerData) {
        Object.entries(footerRows).forEach(([rowName, rowTitle], rowIndex) => {
          const DEFAULT_ROW_CONTENT = "-";
          const $row = $footerRows[rowIndex];
          const $cells = [...$row.children];
          $cells.forEach(($cell, cellIndex) => {
            const cellData = footerData[cellIndex];
            const isHeaderCell = cellIndex === 0;
            if (!cellData) return ($cell.innerHTML = isHeaderCell ? rowTitle : DEFAULT_ROW_CONTENT);
            const rowData = cellData.find((data) => data.rowName === rowName);
            if (!rowData) return;
            $cell.innerHTML = rowData.content ?? DEFAULT_ROW_CONTENT;
          });
        });
      }
    }
  }

  reborn(createTableArgs) {
    gpssat.AppTable._destroy(this.id);
    const $table = document.getElementById(this.id);
    $table.replaceWith(this.$originalTableSnapshot);
    const rebornedTable = gpssat.AppTable.createTable({...createTableArgs, tableDOMUniqueId: this.id});
    return rebornedTable;
  }

  dispose() {
    gpssat.AppTable._destroy(this.id);
  }
}

class AppTable {
  static _tables = [];
  _prefix = null;
  _tableFooterCallbacks = [];
  _tableDrawCallbacks = [];
  _tableContextMenu = [];

  constructor() {
    this._tables = AppTable._tables;
    this._initSelectTable();
    setColumnVisibilityTogglesToAffectTheFooter();

    function setColumnVisibilityTogglesToAffectTheFooter() {
      const defaultToggleColumnAction = DataTable.ext.buttons.columnVisibility.action;
      DataTable.ext.buttons.columnVisibility.action = function (...args) {
        defaultToggleColumnAction.call(this, ...args);
        const tableInstance = this.table();
        Object.assign(tableInstance, {
          0: tableInstance.node(),
          api: function () {
            return this;
          },
        });
        const footerCallback = tableInstance.context[0].aoFooterCallback[0].fn;
        footerCallback.call(tableInstance);
      };
    }
  }

  static get(partialId) {
    if (!partialId) return;
    const tables = Object.values(AppTable._tables);
    return tables.find((table) => table?.table?.()?.node?.()?.id?.includes?.(partialId));
  }

  createTable({
                tableDOMUniqueId,
                url,
                columns,
                footer: footerOptions,
                title,
                exclude,
                orientation,
                headerFooter,
                saveState,
                dom,
                iDisplayLength,
                adicionalButtons,
                dataPrefix,
                toggleable = true,
                formatter,
                columnsRenderer,
              }) {
    const columnsDef = Array.isArray(columns)
      ? columns
      : Object.entries(columns).map(([columnName, columnData]) => {
        return {
          defaultContent: "-",
          ...columnData,
          name: columnName,
        };
      });
    const tableInstance = this._initTable(
      tableDOMUniqueId,
      tableDOMUniqueId,
      url,
      columnsDef,
      title,
      exclude,
      orientation,
      headerFooter,
      saveState,
      dom,
      iDisplayLength,
      adicionalButtons,
      dataPrefix,
      toggleable,
      footerOptions,
      formatter,
      true,
      columnsRenderer,
    );
    return tableInstance;
  }

  _initTable(
    pageId,
    id,
    url,
    columns,
    title,
    exclude,
    orientation,
    _headerFooter,
    _saveState,
    _dom,
    iDisplayLength,
    adicionalButtons,
    dataPrefix,
    toggleable,
    footerOptions,
    formatter,
    isEnhancedTable,
    columnsRenderer,
  ) {
    const that = this;
    if (typeof adicionalButtons === "string") adicionalButtons = JSON.parse(adicionalButtons);
    if (typeof columns === "string") columns = JSON.parse(columns);
    if (Array.isArray(columns)) {
      columns = (columns || []).map((column) => {
        if (columnsRenderer) {
          const renderer = columnsRenderer[column.data];
          if (renderer) column.render = renderer;
        }
        if (column.className) {
          const classNames = column.className.split(" ");
          if (classNames.includes("details-control")) column.render = () => '<i class="fa fa-plus"></i>';
        }
        if (column.columnTitle) column.title = column.columnTitle;
        return column;
      });
    }
    const exportOptions = {modifier: {page: "current"}, columns: ":visible"};
    const buttons = [
      {
        extend: "colvis",
        className: "btn-sm",
        collectionLayout: "fixed four-column",
        columns: exclude ? `:gt(${exclude})` : "",
        text: Lang.get("datatable.button1"),
      },
      {
        title: moment().format("x"),
        extend: "excelHtml5",
        footer: true,
        className: "btn-sm",
        text: Lang.get("datatable.button2"),
        exportOptions,
        customize: (xlsx) => {
          const sheet = xlsx.xl.worksheets["sheet1.xml"];
          window.temp1 = sheet;
          const lastRow = sheet.querySelector("sheetData").lastElementChild;
          [...lastRow.children].forEach((child) => {
            const tElement = child.querySelector("t");
            if (!tElement) return;
            const formatedWithExcelLineBreak = tElement.innerHTML.replace(" ", "\n");
            tElement.innerHTML = formatedWithExcelLineBreak;
          });
        },
      },
      {
        extend: "pdfHtml5",
        footer: true,
        className: "btn-sm",
        text: Lang.get("datatable.button3"),
        title,
        pageSize: "A4",
        orientation: orientation ? orientation : "landscape",
        exportOptions,
        filename: moment().format("x"),
        customize: (doc) => {
          doc.content[1].table.body.slice(-1)[0].forEach((cell) => (cell.text = cell.text.replace(" ", "\n")));

          const colCount = [];
          const lines = $(`#${id}`).find("tbody tr:first-child td");
          lines.each(function () {
            const colSpan = $(this).attr("colspan");
            if (!colSpan) return colCount.push(`${100 / lines.length}%`);
            colCount.concat(Array.from({length: colSpan}).fill("*"));
          });
          doc.content[1].table.widths = colCount;
          doc.pageMargins = [15, 20, 15, 20];
          doc.footer = {
            columns: [
              {
                text: `${Lang.get("form.user")}: ${$("meta[name=user]").attr("content")}`,
                alignment: "left",
                margin: [10, 0, 0, 0],
              },
              {
                text: `${Lang.get("form.date")}: ${moment().format("DD/MM/YYYY HH:mm:ss")}`,
                alignment: "right",
                margin: [0, 0, 10, 0],
              },
            ],
          };
        },
      },
      {
        extend: "print",
        footer: true,
        className: "btn-sm",
        text: Lang.get("datatable.button4"),
        title,
        exportOptions,
      },
      ...(adicionalButtons || []),
    ];
    const options = {
      columns,
      ajax: url,
      serverSide: Boolean(url),
      stateSave: true,
      stateDuration: 0,
      formatter,
      processing: true,
      deferRender: true,
      dom: `<'row'<'col-sm-12 col-md-2'l><'col-sm-12 col-md-3'f><'col-sm-12 col-md-7 dataTables_buttons'B>>
<'row'<'col-sm-12'tr>>
<'row'<'col-sm-12 col-md-5'i><'col-sm-12 col-md-7'p>>`,
      order: [],
      iDisplayLength: iDisplayLength ?? 25,
      aLengthMenu: [
        [15, 25, 50, 100, 200, -1],
        [15, 25, 50, 100, 200, Lang.get('datatable.all')],
      ],
      select: {
        toggleable: toggleable ?? true,
        className: "table-item--selected",
      },
      buttons,
      language: {
        sEmptyTable: Lang.get("datatable.sEmptyTable"),
        sInfo: Lang.get("datatable.sInfo"),
        sInfoEmpty: Lang.get("datatable.sInfoEmpty"),
        sInfoFiltered: Lang.get("datatable.sInfoFiltered"),
        sInfoPostFix: Lang.get("datatable.sInfoPostFix"),
        sInfoThousands: Lang.get("datatable.sInfoThousands"),
        sLengthMenu: Lang.get("datatable.sLengthMenu"),
        sLoadingRecords: Lang.get("datatable.sLoadingRecords"),
        sProcessing: Lang.get("datatable.sProcessing"),
        sZeroRecords: Lang.get("datatable.sZeroRecords"),
        sSearch: Lang.get("datatable.sSearch"),
        oPaginate: {
          sNext: Lang.get("datatable.sNext"),
          sPrevious: Lang.get("datatable.sPrevious"),
          sFirst: Lang.get("datatable.sFirst"),
          sLast: Lang.get("datatable.sLast"),
        },
        oAria: {
          sSortAscending: Lang.get("datatable.sSortAscending"),
          sSortDescending: Lang.get("datatable.sSortDescending"),
        },
      },
      footerCallback: function (_row, _data, _start, _end, _display) {
        if (this.api().init().footerOptions) return this.api().init().renderFooter?.();
        if (typeof that._tableFooterCallbacks[pageId] === "function") that._tableFooterCallbacks[pageId](this);
        else that._tableFooterCallbacks[pageId] = that._defaultFooterCallback(pageId, dataPrefix, this, columns);
      },
      drawCallback: function (_settings) {
        if (typeof that._tableDrawCallbacks[pageId] === "function") that._tableDrawCallbacks[pageId](this);
        else that._tableDrawCallbacks[pageId] = that._defaultDrawCallback(pageId, dataPrefix, this, columns);
        $(`#${id} tbody tr`).contextMenu("destroy");
        if (!that._tableContextMenu[pageId]) return;
        const items = that._setContextMenuItems(pageId);
        if (!items.length) return;
        $.contextMenu({selector: `#${id} tbody tr`, items});
      },
    };
    $.extend($.fn.dataTableExt.oSort, {
      "date-euro-asc": (a, b) => a - b,
      "date-euro-desc": (a, b) => b - a,
      "date-euro-pre": (a) => {
        if ($.trim(a) === "") return Number.Infinity;
        const frDatea = $.trim(a).split(" ");
        const frTimea = undefined !== frDatea[1] ? frDatea[1].split(":") : [0, 0, 0];
        const frDatea2 = frDatea[0].split("/");
        return (
          (frDatea2[2] + frDatea2[1] + frDatea2[0] + frTimea[0] + frTimea[1] + (undefined !== frTimea[2] ? frTimea[2] : 0)) *
          1
        );
      },
    });
    $.fn.dataTable.moment("DD/MM/YYYY");
    $.fn.dataTable.moment("DD/MM/YYYY HH:mm:ss");
    $.fn.dataTable.moment("DD/MM/YYYY HH:mm:ss (Z)");
    $(`[data-content="tabs-pages-${pageId}"] .table-responsive`).scroll(function () {
      const distance = $(this).scrollLeft() - 26;
      $(`[aria-describedby="${id}_info"]`).css("left", distance * -1);
    });
    const instance = $(`#${id}`).DataTable(options);
    if (isEnhancedTable) {
      const $originalTableSnapshot = document.getElementById(id).cloneNode();
      const enhancedTable = new EnhancedTable({id: pageId, instance, footerOptions, $originalTableSnapshot});
      this._tables[pageId] = enhancedTable;
      return enhancedTable;
    }
    this._tables[pageId] = instance;
    return instance;
  }

  _initSelectTable() {
    $("body").delegate('table.table:not(".table-noselect") tbody tr', "click", function () {
      try {
        if ($(this).hasClass("selected")) return $(this).removeClass("selected");
        if (!gpssat.AppTable._tables[gpssat.AppTable._getTableId()]) return;
        gpssat.AppTable._tables[gpssat.AppTable._getTableId()].$("tr.selected").removeClass("selected");
        $(this).addClass("selected");
      } catch (e) {
        console.error(e);
      }
    });
  }

  _setFooterCallback(pageId, callback) {
    this._tableFooterCallbacks[pageId] = callback;
  }

  _setDrawCallback(pageId, callback) {
    this._tableDrawCallbacks[pageId] = callback;
  }

  _setContextMenu(pageId, show) {
    this._tableContextMenu[pageId] = show;
  }

  _setPrefix(value) {
    this._prefix = value;
  }

  _getTableId() {
    return window.gpssat.AppPage._tabsActionId + (this._prefix ? this._prefix : "");
  }

  _destroy(pageId) {
    this._tables[pageId].rows().invalidate();
    this._tables[pageId].clear();
    this._tables[pageId].destroy();
    delete this._tables[pageId];
  }

  _setContextMenuItems(pageId) {
    const items = [];
    $(`[data-content="tabs-pages-${pageId}"] #actions-buttons a`).each(function (index, value) {
      const $btn = $(this);
      const name = $btn.data("original-title");
      items[index] = {
        name,
        callback: function () {
          if (!$(this).hasClass("selected")) $(this).click();
          $btn.click();
        },
      };
    });
    return items;
  }

  _defaultFooterCallback(pageId, dataPrefix, callback, columnsGrid) {
    const api = callback.api();
    let columns;
    if (typeof columnsGrid === "object") columns = columnsGrid;
    else columns = JSON.parse(columnsGrid);

    function sum(c, time = false) {
      return api
        .column(c, {search: "applied"})
        .data()
        .reduce((a, b) => {
          if (!time) return intVal(a) + intVal(b);
          if (Number.isNaN(a)) a = gpssat.App._checkIfItShouldBeFormatedToDecimal(pageId, dataPrefix, a);
          if (Number.isNaN(b)) b = gpssat.App._checkIfItShouldBeFormatedToDecimal(pageId, dataPrefix, b);
          return intVal(a) + intVal(b);
        }, 0)
        .toFixed(2);

      function intVal(i) {
        return typeof i === "string" ? i.replace(/[\$,]/g, "") * 1 : typeof i === "number" ? i : 0;
      }
    }

    function average(c, time = false) {
      if (!api.column(c, {search: "applied"}).data().length) return "-";
      const size = api
        .column(c, {search: "applied"})
        .data()
        .filter((value) => value).length;
      return (Number.parseFloat(sum(c, time)) / size).toFixed(2);
    }

    api.columns().every((i) => {
      let total = "-";
      let avg = "-";
      if (!columns) return;
      if (columns[i].total) total = sum(i, columns[i].time);
      if (columns[i].average) avg = average(i, columns[i].time);
      // biome-ignore lint/suspicious/noGlobalIsNan: <The intended funcionality is of the `iNaN()` global, not of the `Number.isNaN()`, which is slightly different and caused edge-case hard to find bugs>
      if (isNaN(total) && isNaN(avg)) return;
      if (columns[i].time) {
        total = gpssat.App._checkIfItShouldBeFormatedToTime(pageId, dataPrefix, total);
        avg = gpssat.App._checkIfItShouldBeFormatedToTime(pageId, dataPrefix, avg);
      }
      $(api.column(i).footer()).html(`${total}<br/> ${avg}`);
    });
  }

  _defaultDrawCallback(pageId, dataPrefix, callback, columnsGrid) {
    const api = callback.api();
    let columns;
    if (typeof columnsGrid === "object") columns = columnsGrid;
    else columns = JSON.parse(columnsGrid);
    api.columns().every((i) => {
      if (!columns) return;
      if (columns[i].color) {
        api.rows({column: i, page: "current"}).every((k) => {
          const num = api.cell({row: k, column: i}).data();
          $(api.cell({row: k, column: i}).node()).css({color: num >= 0 ? "green" : "red"});
        });
      }
      if (columns[i].time) {
        api.rows({column: i, page: "current"}).every((k) => {
          const num = api.cell({row: k, column: i}).data();
          if (Number.isNaN(num)) return;
          const time = gpssat.App._checkIfItShouldBeFormatedToTime(pageId, dataPrefix, num);
          api.cell({row: k, column: i}).data(time);
          const index = columns.findIndex((column) => column.data === columns[i].data);
          if (index > 0 && index !== i) api.cell({row: k, column: index}).data(num);
        });
      }
      if (columns[i].overwrite)
        api.rows({column: i, page: "current"}).every((k) => api.cell({row: k, column: i}).data(columns[i].overwrite));
    });
  }
}

window.gpssat.AppTable = new AppTable();
